import React, { useState } from 'react';
import { CertificateForm } from './modules/certificate-form';
import { CertificatePreview } from './modules/certificate-preview';
import { TestReport } from './modules/test-report';
import { FileManager } from './modules/file-manager';
import { CertificateInfo, TestReportData } from './types/certificate';
import './styles/global.css';

/**
 * 主应用组件
 * 产品合格证生成系统
 */
function App() {
  const [currentView, setCurrentView] = useState<'form' | 'preview' | 'report' | 'files'>('form');
  const [certificateData, setCertificateData] = useState<CertificateInfo | null>(null);

  // 处理表单提交的回调函数
  const handleCertificateSubmit = async (data: CertificateInfo) => {
    console.log('收到表单数据:', data);
    setCertificateData(data);
    setCurrentView('preview');
  };

  // 转换合格证数据为检测报告数据
  const getTestReportData = (): TestReportData | null => {
    if (!certificateData) return null;
    
    return {
      orderNumber: certificateData.orderNumber,
      quantity: certificateData.bucketCount,
      batchNumber: certificateData.batchNumber,
      productionDate: certificateData.productionDate,
      generationDate: certificateData.testReportDate
    };
  };

  // 导航项配置
  const navItems = [
    { 
      key: 'form', 
      label: '产品信息录入', 
      icon: '📝', 
      color: '#28a745',
      disabled: false 
    },
    { 
      key: 'preview', 
      label: '合格证预览', 
      icon: '📜', 
      color: '#007bff',
      disabled: !certificateData 
    },
    { 
      key: 'report', 
      label: '检测报告', 
      icon: '📊', 
      color: '#17a2b8',
      disabled: !certificateData 
    },
    { 
      key: 'files', 
      label: '文件管理', 
      icon: '📁', 
      color: '#6f42c1',
      disabled: false 
    }
  ];

  return (
    <div className="App">
      {/* 页面头部 */}
      <header className="app-header">
        <div className="container">
          <div className="header-content">
            <div className="logo-section">
              <h1 className="page-title">
                <span className="title-icon">🏭</span>
                产品合格证生成系统
              </h1>
              <p className="subtitle">专业、高效、可靠的质量管理解决方案</p>
            </div>
          </div>
          
          {/* 导航栏 */}
          <nav className="main-nav">
            <div className="nav-container">
              {navItems.map((item) => (
                <button 
                  key={item.key}
                  onClick={() => setCurrentView(item.key as any)}
                  className={`nav-button ${currentView === item.key ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`}
                  disabled={item.disabled}
                  style={{
                    '--nav-color': item.color
                  } as React.CSSProperties}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                  {item.disabled && <span className="nav-badge">需要数据</span>}
                </button>
              ))}
            </div>
          </nav>
        </div>
      </header>

      {/* 主要内容区域 */}
      <main className="main-content">
        <div className="container">
          <div className="content-wrapper">
            {currentView === 'form' && (
              <div className="page-section">
                <div className="section-header">
                  <h2 className="section-title">
                    <span className="section-icon">📝</span>
                    产品信息录入
                  </h2>
                  <p className="section-description">请填写产品相关信息，系统将自动生成合格证和检测报告</p>
                </div>
                <CertificateForm 
                  onSubmit={handleCertificateSubmit}
                  className="main-form"
                />
              </div>
            )}
            
            {currentView === 'preview' && certificateData && (
              <div className="page-section">
                <div className="section-header">
                  <h2 className="section-title">
                    <span className="section-icon">📜</span>
                    合格证预览
                  </h2>
                  <p className="section-description">订单号: {certificateData.orderNumber}</p>
                </div>
                <CertificatePreview 
                  certificateData={certificateData}
                  onDownloadPDF={() => console.log('PDF已下载')}
                />
              </div>
            )}
            
            {currentView === 'report' && getTestReportData() && (
              <div className="page-section">
                <div className="section-header">
                  <h2 className="section-title">
                    <span className="section-icon">📊</span>
                    检测报告
                  </h2>
                  <p className="section-description">订单号: {getTestReportData()!.orderNumber}</p>
                </div>
                <TestReport 
                  reportData={getTestReportData()!}
                />
              </div>
            )}
            
            {currentView === 'files' && (
              <div className="page-section">
                <div className="section-header">
                  <h2 className="section-title">
                    <span className="section-icon">📁</span>
                    文件管理
                  </h2>
                  <p className="section-description">管理所有生成的合格证和检测报告文件</p>
                </div>
                <FileManager />
              </div>
            )}
            
            {(currentView === 'preview' || currentView === 'report') && !certificateData && (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <h3 className="empty-title">请先填写产品信息</h3>
                <p className="empty-description">
                  您需要先在产品信息录入页面填写相关信息，然后才可以查看
                  {currentView === 'preview' ? '合格证预览' : '检测报告'}
                </p>
                <button 
                  onClick={() => setCurrentView('form')}
                  className="empty-action-btn"
                >
                  <span>📝</span>
                  前往信息录入
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* 页脚 */}
      <footer className="app-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-info">
              <p>&copy; 2025 产品合格证生成系统</p>
              <span className="footer-version">版本 1.0.0</span>
            </div>
            <div className="footer-links">
              <span className="footer-status">
                <span className="status-dot"></span>
                系统运行正常
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;